import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, MessageCircle, ShoppingBag } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { createWhatsAppLink, DEFAULT_WHATSAPP_MESSAGE } from "@/lib/whatsapp";
import { supabase } from "@/supabase";

interface Slide {
  id: string;
  title: string;
  subtitle: string | null;
  image_url: string | null;
  image_url_mobile: string | null;
  button_text: string | null;
  button_link: string | null;
  whatsapp_text: string | null;
  is_active: boolean;
  display_order: number;
}

const HeroCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Buscar slides do Supabase
  const { data: slides, isLoading } = useQuery({
    queryKey: ["carousel-slides"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("carousel_slides")
        .select("*")
        .eq("is_active", true)
        .order("display_order", { ascending: true });
      
      if (error) throw error;
      return (data || []) as Slide[];
    },
  });

  useEffect(() => {
    if (!slides || slides.length <= 1) return;
    
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % slides.length);
    }, 6000);

    return () => clearInterval(timer);
  }, [slides?.length]);

  const goToPrevious = () => {
    if (!slides) return;
    setCurrentIndex((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const goToNext = () => {
    if (!slides) return;
    setCurrentIndex((prev) => (prev + 1) % slides.length);
  };

  // Loading state
  if (isLoading) {
    return (
      <section id="inicio" className="relative min-h-screen flex items-center pt-16 md:pt-20">
        <div className="absolute inset-0 z-0 hero-gradient" />
        <div className="relative z-10 container-narrow">
          <div className="max-w-2xl space-y-4">
            <div className="h-12 bg-muted rounded animate-pulse" />
            <div className="h-6 bg-muted rounded w-3/4 animate-pulse" />
            <div className="flex gap-4">
              <div className="h-12 bg-muted rounded w-40 animate-pulse" />
              <div className="h-12 bg-muted rounded w-40 animate-pulse" />
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Se não houver slides, não renderizar nada
  if (!slides || slides.length === 0) {
    return null;
  }

  const currentSlide = slides[currentIndex];

  return (
    <section id="inicio" className="relative min-h-screen flex items-center pt-16 md:pt-20">
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8 }}
            className="absolute inset-0"
          >
            {/* Imagem Desktop (hidden em mobile) */}
            {currentSlide.image_url && (
              <img
                src={currentSlide.image_url}
                alt=""
                className="hidden md:block w-full h-full object-cover"
              />
            )}
            
            {/* Imagem Mobile (hidden em desktop) */}
            {currentSlide.image_url_mobile ? (
              <img
                src={currentSlide.image_url_mobile}
                alt=""
                className="md:hidden w-full h-full object-cover"
              />
            ) : currentSlide.image_url ? (
              <img
                src={currentSlide.image_url}
                alt=""
                className="md:hidden w-full h-full object-cover"
              />
            ) : null}
            
            <div className="absolute inset-0 " />
            <div className="absolute inset-0 bg-gradient-to-r from-background/50 via-background/30 to-transparent" />
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Content */}
      <div className="relative z-10 container-narrow">
        <div className="max-w-2xl">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentIndex}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold text-foreground leading-tight mb-6 text-balance">
                {currentSlide.title}
              </h1>
              {currentSlide.subtitle && (
                <p className="text-lg md:text-xl text-foreground/80 mb-8 leading-relaxed">
                  {currentSlide.subtitle}
                </p>
              )}

              {/* Feature bullets */}
              <div className="flex flex-wrap gap-4 mb-10">
                {["Produção artesanal", "Peças exclusivas", "Feito com carinho"].map((feature) => (
                  <span
                    key={feature}
                    className="inline-flex items-center gap-2 text-sm text-foreground/70"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-golden" />
                    {feature}
                  </span>
                ))}
              </div>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href={currentSlide.button_link || "#produtos"}
                  className="inline-flex items-center justify-center gap-2 btn-primary"
                >
                  <ShoppingBag className="w-5 h-5" />
                  {currentSlide.button_text || "Comprar agora"}
                </a>
                <a
                  href={createWhatsAppLink(DEFAULT_WHATSAPP_MESSAGE)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 btn-whatsapp"
                >
                  <MessageCircle className="w-5 h-5" />
                  {currentSlide.whatsapp_text || "Falar no WhatsApp"}
                </a>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Navigation Arrows */}
      {slides.length > 1 && (
        <>
          <button
            onClick={goToPrevious}
            className="absolute left-4 top-1/2 -translate-y-1/2 z-20 p-3 rounded-full bg-background/80 text-foreground hover:bg-background transition-colors"
            aria-label="Slide anterior"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={goToNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 z-20 p-3 rounded-full bg-background/80 text-foreground hover:bg-background transition-colors"
            aria-label="Próximo slide"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </>
      )}

      {/* Dots Indicator */}
      {slides && slides.length > 1 && (
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-2">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`w-2.5 h-2.5 rounded-full transition-all ${
                index === currentIndex
                  ? "bg-primary w-8"
                  : "bg-foreground/30 hover:bg-foreground/50"
              }`}
              aria-label={`Ir para slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </section>
  );
};

export default HeroCarousel;